import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getExamDiagnosis, onExamSubmit, type ExamDiagnosisResponse, type KpBreakdownItem } from '../../api/exam'
import { getQuickSummary } from '../../api/aiTeam'
// generateFromDiagnosis removed — course content is now generated offline by skill
import { ROUTES, generatePath } from '@/constants/routes'
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Loader2, CheckCircle, XCircle, Minus, ArrowLeft, BookOpen, Target, RotateCcw, Brain } from 'lucide-react'

const ABILITY_LABELS: Record<string, string> = {
  vocabulary: '词汇能力',
  grammar: '语法能力',
  reading: '阅读理解',
  listening: '听力能力',
  writing: '写作表达',
}

const ERROR_REASON_LABELS: Record<string, string> = {
  word_unknown: '单词不认识',
  collocation: '固定搭配不会',
  grammar_confused: '语法点不清',
  misread: '审题错误',
  locate_error: '定位错误',
  inference_lack: '推理不足',
  time_pressure: '时间不够',
  writing_weak: '写作表达弱',
  careless: '粗心',
  concept_confused: '概念混淆',
  method_wrong: '方法错误',
  knowledge_gap: '知识盲区',
}

// ─── Loading messages (friendly, time-based) ─────────────────────

const LOADING_MESSAGES = [
  { threshold: 0, text: '别急，让我仔细看看你的答题情况...' },
  { threshold: 5000, text: '正在分析你的错题规律，找找问题出在哪...' },
  { threshold: 15000, text: '快好了，正在为你量身定制学习计划...' },
  { threshold: 30000, text: '再等一小会儿，马上就好啦~' },
]

function useLoadingMessage(isLoading: boolean) {
  const [message, setMessage] = useState(LOADING_MESSAGES[0].text)
  const startTimeRef = useRef(Date.now())

  useEffect(() => {
    if (!isLoading) return
    startTimeRef.current = Date.now()
    setMessage(LOADING_MESSAGES[0].text)

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current
      const msg = [...LOADING_MESSAGES].reverse().find(m => elapsed >= m.threshold)
      if (msg) setMessage(msg.text)
    }, 1000)

    return () => clearInterval(interval)
  }, [isLoading])

  return message
}

// ─── Severity helpers ────────────────────────────────────────────

const SEVERITY_COLORS = {
  high: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', bar: 'bg-red-500', label: '需加强' },
  medium: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', bar: 'bg-yellow-500', label: '待巩固' },
  low: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', bar: 'bg-green-500', label: '已掌握' },
}

function getSeverity(accuracy: number): 'high' | 'medium' | 'low' {
  if (accuracy < 0.6) return 'high'
  if (accuracy < 0.8) return 'medium'
  return 'low'
}

function getBarColor(accuracy: number): string {
  if (accuracy >= 0.8) return 'bg-green-500'
  if (accuracy >= 0.6) return 'bg-yellow-500'
  return 'bg-red-500'
}

// ─── Main Component ──────────────────────────────────────────────

export default function ExamDiagnosisPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()

  const [data, setData] = useState<ExamDiagnosisResponse | null>(null)
  const [quickSummary, setQuickSummary] = useState<string>('')
  const [phase, setPhase] = useState<'loading' | 'basic' | 'analyzing' | 'complete'>('loading')
  const [error, setError] = useState<string | null>(null)

  const loadingMessage = useLoadingMessage(phase === 'analyzing')

  // Initial fetch
  useEffect(() => {
    if (!sessionId) return
    loadData()
  }, [sessionId])

  async function loadData() {
    try {
      setPhase('loading')
      // Parallel fetch: diagnosis data + quick summary
      const [result, summaryResult] = await Promise.all([
        getExamDiagnosis(sessionId!),
        getQuickSummary(sessionId!).catch(() => ({ summary: '' })),
      ])
      setData(result)
      if (summaryResult.summary) setQuickSummary(summaryResult.summary)

      if (result.diagnosis) {
        // AI analysis already exists
        setPhase('complete')
      } else {
        // Show basic info, then trigger AI analysis
        setPhase('basic')
        triggerAIAnalysis()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败')
      setPhase('basic')
    }
  }

  async function triggerAIAnalysis() {
    try {
      setPhase('analyzing')
      const result = await onExamSubmit(sessionId!)
      // Reload to get full data
      const fullData = await getExamDiagnosis(sessionId!)
      setData(fullData)
      setPhase('complete')
    } catch (err) {
      console.error('AI analysis failed:', err)
      // Stay on basic phase - user can still see score + knowledge points
      setPhase('basic')
    }
  }

  if (error && !data) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-3xl">
        <div className="text-center py-12">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={() => navigate(ROUTES.EXAM_HISTORY)}>返回考试历史</Button>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  const { session, knowledgeSummary, diagnosis, courses } = data

  return (
    <div className="container mx-auto py-8 px-4 max-w-3xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(ROUTES.EXAM_HISTORY)}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          返回考试历史
        </button>
        <h1 className="text-2xl font-bold text-slate-800">考试诊断报告</h1>
      </div>

      {/* Score Overview */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="text-center mb-4">
            <div className="text-5xl font-bold text-slate-800">
              {session.totalScore ?? 0}
            </div>
            <div className="text-sm text-slate-500 mt-1">总分</div>
          </div>
          <div className="flex justify-center gap-8 text-sm">
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-slate-600">正确 {session.correctCount ?? 0}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <XCircle className="w-4 h-4 text-red-500" />
              <span className="text-slate-600">错误 {session.wrongCount ?? 0}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Minus className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">未答 {session.unansweredCount ?? 0}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overall Summary (AI or quick) */}
      {(data.diagnosis?.overallSummary || quickSummary) && (
        <Card className="mb-6 border-indigo-200 bg-indigo-50/30">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-700 leading-relaxed">
              {data.diagnosis?.overallSummary || quickSummary}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Knowledge Point Breakdown */}
      {knowledgeSummary && knowledgeSummary.kpBreakdown.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5" />
              知识点掌握情况
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {knowledgeSummary.kpBreakdown.map((kp) => (
                <KpBreakdownRow key={kp.kpCode} item={kp} />
              ))}
            </div>
            {knowledgeSummary.untaggedCount > 0 && (
              <p className="text-xs text-slate-400 mt-3">
                * {knowledgeSummary.untaggedCount} 道题未标记知识点，未纳入分析
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* AI Analysis Section */}
      {phase === 'analyzing' && (
        <Card className="mb-6 border-blue-200 bg-blue-50/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              <div>
                <p className="text-sm font-medium text-blue-700">{loadingMessage}</p>
                <p className="text-xs text-blue-500 mt-1">AI 正在深度分析中，你可以先看看上面的知识点掌握情况</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Results: Question Type Breakdown */}
      {phase === 'complete' && diagnosis?.questionTypeBreakdown && diagnosis.questionTypeBreakdown.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5" />
              题型统计
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {diagnosis.questionTypeBreakdown.map((qt) => (
                <KpBreakdownRow key={qt.type} item={{ kpCode: qt.type, name: qt.name, total: qt.total, correct: qt.correct, accuracy: qt.accuracy, severity: getSeverity(qt.accuracy) }} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Results: Ability Profile */}
      {phase === 'complete' && diagnosis?.abilityProfile && Object.keys(diagnosis.abilityProfile).length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Brain className="w-5 h-5" />
              能力维度诊断
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(diagnosis.abilityProfile).map(([key, ability]) => (
                <AbilityRow key={key} label={ABILITY_LABELS[key] ?? key} ability={ability} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Results: Error Annotations */}
      {phase === 'complete' && diagnosis?.errorAnnotations && diagnosis.errorAnnotations.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              错题分析
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {diagnosis.errorAnnotations.map((ann, idx) => (
                <div key={idx} className="p-3 rounded-lg border border-slate-200 bg-white">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-slate-500">第 {idx + 1} 题</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-700">{ERROR_REASON_LABELS[ann.errorReason] ?? ann.errorReason}</span>
                  </div>
                  <p className="text-sm text-slate-600">{ann.detail}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Results: Mistake Analysis */}
      {phase === 'complete' && diagnosis?.mistakeAnalysis && (
        <MistakeAnalysisCard analysis={diagnosis.mistakeAnalysis} />
      )}

      {/* AI Results: Weak Points */}
      {phase === 'complete' && diagnosis && diagnosis.mistakeAnalysis?.priorityWeakPoints?.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              薄弱知识点详情
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {diagnosis.mistakeAnalysis.priorityWeakPoints.map((wp: any) => (
                <WeakPointCard
                  key={wp.kpCode}
                  weakPoint={wp}
                  sessionId={sessionId!}
                  diagnosisId={data.diagnosisId}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Results: Learning Plan */}
      {phase === 'complete' && diagnosis && diagnosis.learningPlan && (
        <LearningPlanCard
          plan={diagnosis.learningPlan}
          courses={courses}
        />
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 mt-6">
        <Link to={ROUTES.MISTAKES} className="flex-1">
          <Button variant="outline" className="w-full">
            <RotateCcw className="w-4 h-4 mr-2" />
            查看错题本
          </Button>
        </Link>
        <Link to={ROUTES.EXAM} className="flex-1">
          <Button variant="outline" className="w-full">
            返回试卷列表
          </Button>
        </Link>
      </div>
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────────

function KpBreakdownRow({ item }: { item: KpBreakdownItem }) {
  const severity = getSeverity(item.accuracy)
  const colors = SEVERITY_COLORS[severity]
  const pct = Math.round(item.accuracy * 100)

  return (
    <div className={`p-3 rounded-lg ${colors.bg} border ${colors.border}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-slate-700">{item.name}</span>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded ${colors.text} ${colors.bg}`}>
            {colors.label}
          </span>
          <span className="text-sm font-bold text-slate-700">{pct}%</span>
        </div>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${getBarColor(item.accuracy)}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-xs text-slate-500 mt-1">
        {item.correct}/{item.total} 题正确
      </div>
    </div>
  )
}

function MistakeAnalysisCard({ analysis }: { analysis: any }) {
  if (!analysis.byErrorType) return null

  const errorTypes = [
    { key: 'conceptConfused', label: '概念混淆', color: 'bg-red-100 text-red-700' },
    { key: 'carelessError', label: '粗心错误', color: 'bg-yellow-100 text-yellow-700' },
    { key: 'methodWrong', label: '方法错误', color: 'bg-orange-100 text-orange-700' },
    { key: 'knowledgeGap', label: '知识盲区', color: 'bg-purple-100 text-purple-700' },
  ]

  const total = Object.values(analysis.byErrorType).reduce((s: number, v: any) => s + (v as number), 0)

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-lg">错题归因分析</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2">
          {errorTypes.map((et) => {
            const count = analysis.byErrorType[et.key] ?? 0
            if (count === 0) return null
            const pct = total > 0 ? Math.round((count / total) * 100) : 0
            return (
              <div key={et.key} className={`p-3 rounded-lg ${et.color}`}>
                <div className="text-lg font-bold">{count}题</div>
                <div className="text-xs">{et.label} ({pct}%)</div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

function WeakPointCard({ weakPoint, sessionId, diagnosisId }: { weakPoint: any; sessionId: string; diagnosisId: string | null }) {
  const navigate = useNavigate()
  const severityColors: Record<string, string> = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-blue-100 text-blue-700',
  }

  return (
    <div className="p-4 rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-slate-800">{weakPoint.name}</span>
        <span className={`text-xs px-2 py-0.5 rounded ${severityColors[weakPoint.severity] ?? severityColors.medium}`}>
          {weakPoint.severity === 'high' ? '高优先' : weakPoint.severity === 'medium' ? '中优先' : '低优先'}
        </span>
      </div>
      {weakPoint.reason && (
        <p className="text-sm text-slate-600 mb-2">{weakPoint.reason}</p>
      )}
      {weakPoint.recommendedAction && (
        <p className="text-sm text-blue-600 mb-3">💡 {weakPoint.recommendedAction}</p>
      )}
      <div className="flex gap-2 flex-wrap">
        <span className="text-xs text-slate-500 self-center">讲解课件请使用线下 skill 生成后导入</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(`${ROUTES.TUTOR}?sessionId=${sessionId}&kpCode=${weakPoint.kpCode}`)}
        >
          AI 辅导
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(`${ROUTES.MISTAKES}?kpCode=${weakPoint.kpCode}`)}
        >
          错题复习
        </Button>
      </div>
    </div>
  )
}

function LearningPlanCard({ plan, courses }: { plan: any; courses: ExamDiagnosisResponse['courses'] }) {
  const navigate = useNavigate()

  if (!plan.dailyTasks?.length && !plan.weeklyGoal) return null

  const todayTasks = plan.dailyTasks?.[0]?.tasks ?? []

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          学习计划推荐
        </CardTitle>
      </CardHeader>
      <CardContent>
        {plan.weeklyGoal && (
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 mb-4">
            <p className="text-sm text-blue-700">🎯 {plan.weeklyGoal}</p>
          </div>
        )}

        {todayTasks.length > 0 && (
          <div className="space-y-2 mb-4">
            <p className="text-sm font-medium text-slate-700">📅 今日任务</p>
            {todayTasks.map((task: any, idx: number) => (
              <div key={idx} className="flex items-center gap-3 p-2 rounded bg-slate-50">
                <span className="text-xs text-slate-400 w-5">{idx + 1}</span>
                <div className="flex-1">
                  <span className="text-sm text-slate-700">{task.title}</span>
                  <span className="text-xs text-slate-400 ml-2">{task.duration}分钟</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  task.type === 'lesson' ? 'bg-blue-100 text-blue-700' :
                  task.type === 'practice' ? 'bg-green-100 text-green-700' :
                  task.type === 'retry' ? 'bg-orange-100 text-orange-700' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  {task.type === 'lesson' ? '学习' : task.type === 'practice' ? '练习' : task.type === 'retry' ? '重做' : '复习'}
                </span>
              </div>
            ))}
          </div>
        )}

        {courses.length > 0 && (
          <div className="space-y-2 mb-4">
            <p className="text-sm font-medium text-slate-700">📚 已生成的课程</p>
            {courses.map((course) => (
              <div
                key={course.id}
                className="flex items-center justify-between p-2 rounded bg-slate-50 cursor-pointer hover:bg-slate-100"
                onClick={() => navigate(generatePath(ROUTES.AI_TEAM_COURSE_DETAIL, { courseId: course.id }))}
              >
                <div>
                  <span className="text-sm text-slate-700">{course.title}</span>
                  <span className="text-xs text-slate-400 ml-2">
                    {course.completedLessons}/{course.totalLessons} 课时
                  </span>
                </div>
                <span className="text-xs text-blue-600">继续学习 →</span>
              </div>
            ))}
          </div>
        )}

        <Button
          className="w-full"
          onClick={() => navigate(ROUTES.AI_TEAM_TASKS)}
        >
          查看完整学习计划
        </Button>
      </CardContent>
    </Card>
  )
}

function AbilityRow({ label, ability }: { label: string; ability: { level: string; score: number; description: string } }) {
  const levelColors: Record<string, string> = {
    strong: 'bg-green-500',
    medium: 'bg-yellow-500',
    weak: 'bg-red-500',
  }
  const levelLabels: Record<string, string> = {
    strong: '良好',
    medium: '中等',
    weak: '较弱',
  }

  return (
    <div className="p-3 rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded ${
            ability.level === 'strong' ? 'bg-green-100 text-green-700' :
            ability.level === 'medium' ? 'bg-yellow-100 text-yellow-700' :
            'bg-red-100 text-red-700'
          }`}>
            {levelLabels[ability.level] ?? ability.level}
          </span>
          <span className="text-sm font-bold text-slate-700">{ability.score}</span>
        </div>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-2 mb-1.5">
        <div
          className={`h-2 rounded-full transition-all ${levelColors[ability.level] ?? 'bg-slate-400'}`}
          style={{ width: `${ability.score}%` }}
        />
      </div>
      {ability.description && (
        <p className="text-xs text-slate-500">{ability.description}</p>
      )}
    </div>
  )
}
